import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, Layers, Repeat, Wallet, Users, Zap, CheckCircle2, Rocket, ArrowUpRight, Crown,
} from "lucide-react";
import api from "@/lib/api";

const earnWays = [
  { icon: TrendingUp, title: "ROI Income", desc: "Daily ROI of 0.40%–0.60% based on your package (higher tiers earn more), split 70% USDT + 30% Points, up to a 2X cap." },
  { icon: Zap, title: "ROI Booster 1.5X", desc: "Get 3 direct referrals within 21 days of joining to permanently boost your daily ROI by 1.5X." },
  { icon: Layers, title: "Level Income", desc: "Earn from your team's ROI across 20 deep levels with tiered percentages (10% → 0.25%)." },
  { icon: Repeat, title: "Matching Income", desc: "5% binary matching bonus on your weaker leg, with a $5,000/day cap." },
  { icon: Wallet, title: "Weekly Salary", desc: "Fixed weekly salary from Master to Legend rank ($25 → $12,800/wk) paid for 14–16 weeks per rank." },
  { icon: Crown, title: "Royalty Income", desc: "Get a rank achiever in BOTH legs and earn 10%–28% (by rank) of the income those same-rank achievers generate." },
  { icon: Users, title: "Team Turnover", desc: "From Titan rank and above, earn 0.3%–1% on new team business built across both legs after you achieve the rank." },
];

const levelRows = [
  { range: "Level 1", pct: "10%" }, { range: "Level 2", pct: "5%" }, { range: "Level 3", pct: "4%" },
  { range: "Level 4", pct: "3%" }, { range: "Level 5", pct: "2%" }, { range: "Levels 6–10", pct: "1%" },
  { range: "Levels 11–15", pct: "0.5%" }, { range: "Levels 16–20", pct: "0.25%" },
];

const roadmap = [
  { phase: "2027", title: "Launch Coin & Crypto Payment Card" },
  { phase: "2028", title: "Blockchain Technology" },
  { phase: "2029", title: "Broker House" },
  { phase: "2030+", title: "Global Expansion" },
];

export default function Plans() {
  const [packages, setPackages] = useState([]);
  useEffect(() => { api.get("/plan/packages").then(({ data }) => setPackages(data)).catch(() => {}); }, []);

  return (
    <div className="space-y-8" data-testid="plans-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Compensation Plan</h1>
          <p className="text-slate-400 text-sm mt-1">6 powerful ways to earn on MoneyMoonX.</p>
        </div>
        <Link to="/portal/investments" className="gold-btn px-5 py-3 rounded-xl font-bold text-sm flex items-center gap-2 self-start">
          Buy a Package <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* packages */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {packages.map((p) => (
          <div key={p.id} data-testid={`plan-package-${p.id}`}
            className={`rounded-2xl p-5 flex flex-col ${p.popular ? "bg-gradient-to-b from-[#18150B] to-[#0D0E12] border-2 border-amber-400" : "bg-[#0D0E12] border border-amber-500/20"}`}>
            {p.popular && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-400 mb-1">Most Popular</span>}
            <div className="font-display font-semibold text-amber-300">{p.name}</div>
            <div className="font-mono-fin text-3xl font-extrabold mt-2 gold-text">${p.amount}</div>
            <ul className="mt-3 space-y-1.5 text-xs text-slate-300 flex-1">
              <li className="flex gap-1.5 items-center"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> {p.roi_min.toFixed(2)}%–{p.roi_max.toFixed(2)}% Daily ROI</li>
              <li className="flex gap-1.5 items-center"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> 2X ROI Cap</li>
            </ul>
          </div>
        ))}
      </div>

      {/* 6 ways */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {earnWays.map((w, i) => (
          <div key={w.title} data-testid={`plan-earn-${i}`} className="rounded-2xl bg-[#0D0E12] border border-amber-500/20 p-6 hover:border-amber-500/40 transition-all group">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 grid place-items-center group-hover:scale-110 transition-transform">
              <w.icon className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="font-display font-semibold text-lg mt-4">{w.title}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* level income table */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-4">Level Income (20 Levels)</h3>
          <table className="w-full text-sm">
            <tbody>
              {levelRows.map((r) => (
                <tr key={r.range} className="border-b border-slate-800/50">
                  <td className="py-2.5 text-slate-300">{r.range}</td>
                  <td className="py-2.5 text-right font-mono-fin text-amber-300 font-bold">{r.pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-3">Percentages apply to your downline's daily ROI. Unlock deeper levels with more direct referrals.</p>
        </div>

        {/* terms */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-4">Key Terms</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between border-b border-slate-800/50 pb-2"><span className="text-slate-400">Binary Matching</span><span className="font-mono-fin text-amber-300">5% · $5,000/day cap</span></li>
            <li className="flex justify-between border-b border-slate-800/50 pb-2"><span className="text-slate-400">ROI Cap</span><span className="font-mono-fin text-amber-300">2X of Package</span></li>
            <li className="flex justify-between border-b border-slate-800/50 pb-2"><span className="text-slate-400">Total Income Cap</span><span className="font-mono-fin text-amber-300">3X of Package</span></li>
            <li className="flex justify-between border-b border-slate-800/50 pb-2"><span className="text-slate-400">Min Withdrawal</span><span className="font-mono-fin text-amber-300">$10 · 5% fee</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Processing Time</span><span className="font-mono-fin text-amber-300">24–72 hrs</span></li>
          </ul>
        </div>
      </div>

      {/* roadmap */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-display font-bold text-lg mb-5">Global Expansion Roadmap</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roadmap.map((r) => (
            <div key={r.title} className="rounded-xl bg-black/30 border border-amber-500/15 p-4">
              <div className="w-10 h-10 rounded-lg gold-btn grid place-items-center mb-3"><Rocket className="w-5 h-5 text-black" /></div>
              <div className="text-xs text-amber-400 font-mono-fin">{r.phase}</div>
              <div className="font-display font-semibold mt-1">{r.title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
